import { Component, OnInit, ChangeDetectionStrategy, inject, input, linkedSignal, signal } from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {Client} from '@app/modules/shared/models';
import { FormBuilder, FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import {
  ClientsList,
  getClientError,
  getLoggedInUser,
  getPaginatedClients,
  IAuthenticationState,
  IClientState, IProjectState, ProjectValidateSlug
} from '@app/stores';
import {select, Store} from '@ngrx/store';
import {takeUntil} from 'rxjs/operators';
import {Subject, Subscription} from 'rxjs';
import {ToastrService} from 'ngx-toastr';
import {Observable} from 'rxjs';
import {LoaderService} from '@app/services/loader.service';
import { SbInputFormComponent } from '../../../_components/unified/sb-form/sb-form.component';
import { AutoCompleteSelectComponent } from '../../../_components/auto-complete-select/auto-complete-select.component';

@Component({
  selector: 'app-project-copy',
  templateUrl: './project-copy.component.html',
  styleUrls: ['./project-copy.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SbInputFormComponent, ReactiveFormsModule, AutoCompleteSelectComponent]
})
export class ProjectCopyComponent implements OnInit {
  modal = inject(NgbActiveModal);
  fb = inject(FormBuilder);
  private authenticationStore = inject<Store<IAuthenticationState>>(Store);
  private clientStore = inject<Store<IClientState>>(Store);
  private projectStore = inject<Store<IProjectState>>(Store);
  private toastrService = inject(ToastrService);
  private loaderService = inject(LoaderService);


  /** The clients the opener has loaded already. */
  readonly clients = input<Client[]>([]);
  /** The clients to pick from: the opener's, then each page searched or scrolled to. */
  readonly clientOptions = linkedSignal(() => this.clients());
  public projectForm: FormGroup<{
    title: FormControl<string | null>;
    client_id: FormControl<string | null>;
    with_influencers: FormControl<boolean | null>;
  }>;
  submitted = false;
  unsubscriber = new Subject();
  readonly currentUser = signal<any>(undefined);
  loader$: Observable<boolean>;
  clientSearchValue = '';
  clientPageNumber = 0;
  totalClients = 0;
  slugdata: string;

  constructor() {
    this.subscribeStore();
  }

  get f() {
    return this.projectForm.controls;
  }

  subscribeStore() {
    this.authenticationStore.pipe(select(getLoggedInUser))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(user => {
        if (user) {
          this.currentUser.set(user);
        }
      }
      );

    this.clientStore.pipe(select(getClientError))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(error => {
        if (error) {
          this.toastrService.error(error);
        }
      });

    this.clientStore.pipe(select(getPaginatedClients))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(paginatedClients => {
        if (paginatedClients) {
          this.clientOptions.update((clients) => [...clients, ...paginatedClients.list]);
          this.clientPageNumber = parseInt(paginatedClients.paging.page, 10);
          this.totalClients = paginatedClients.paging.total;
        }
      });
  }

  ngOnInit(): void {
    this.loader$ = this.loaderService.loader$;
    this.projectForm = this.fb.group({
      title: ['', [Validators.required]],
      client_id: ['', this.currentUser()?.can('change-client', 'project') ? [Validators.required] : []],
      with_influencers: [false],
    });
  }

  saveProject() {
    this.submitted = true;
    if (this.projectForm.invalid) {
      return;
    }

    const formData = {
      title: this.projectForm.get('title')?.value,
      client_id: this.projectForm.get('client_id')?.value,
      with_influencers: this.projectForm.get('with_influencers')?.value
    };
    // Only auto-set client_id if user has the permission AND their client_id exists AND no client was selected
    if (this.currentUser()?.can('auto-set-client', 'project') && this.currentUser().client_id && !formData.client_id) {
      this.projectForm.value.client_id = this.currentUser().client_id;
    }

    this.modal.close(this.projectForm.value);
  }

  generateSlug(): void {
    this.slugdata = this.convertToSlug(this.projectForm.get('title').value);
  }

  convertToSlug(Text) {
    return Text.toLowerCase().replace(/[^\w ]+/g, '').replace(/ +/g, '-');
  }

  public validateSlug() {
    if (!this.slugdata) {
      return;
    }
    this.projectStore.dispatch(ProjectValidateSlug({
      params: {
        slug: this.slugdata
      }
    }));
    this.slugdata = null;
  }

  handleSearchValue(searchTerm) {
    this.loader$ = null;
    this.clientSearchValue = searchTerm.term;
    this.clientOptions.set([]);
    this.loadAllPlanners(searchTerm.term);
  }

  handleScroll() {
    this.loader$ = null;
    if (this.clientOptions().length < this.totalClients) {
      this.loadAllPlanners(this.clientSearchValue, this.clientPageNumber);
    }
  }

  loadAllPlanners(search = '', page = 0, perPage = 10, orderBy= {column: 'name', dir: 'asc'}, fields = ['id', 'name']) {
    this.clientPageNumber = page + 1;
    this.clientStore.dispatch(ClientsList({
      params: {
        options: JSON.stringify({includePackage: true, includePagination: true, orderBy, fields}),
        page: this.clientPageNumber,
        perPage,
        search
      }
    }));
  }

  handleClientSelection(selectedClient: any) {
    if (selectedClient) {
      this.projectForm.get('client_id')?.setValue(selectedClient.value);
    } else {
      this.loadAllPlanners();
      this.projectForm.get('client_id')?.setValue(null);
    }
  }
}
